import type { EventCard } from "../types/cards";

export const eventCards: EventCard[] = [
  {
    id: "event_001",
    title: "Medical Bill",
    description: "An unexpected health expense hits.",
    conceptExplanation: "Emergency funds exist for exactly this. Keeping 3–6 months of expenses saved protects you from a debt spiral when life surprises you.",
    deck: "event",
    illustrationKey: "red",
    cashDelta: -40,
    wlbDelta: -1,
    affectsAll: false,
    conditionIcon: "skill_badge",
  },
  {
    id: "event_002",
    title: "Year-End Bonus",
    description: "Your employer rewards your performance.",
    conceptExplanation: "Bonuses are variable income — great when they come, but never count on them for fixed expenses. Save or invest a bonus rather than spending it immediately.",
    deck: "event",
    illustrationKey: "gold",
    cashDelta: 50,
    wlbDelta: 1,
    affectsAll: false,
  },
  {
    id: "event_003",
    title: "Global Recession",
    description: "Markets crash. Everyone feels it.",
    conceptExplanation: "Recessions affect all asset classes. Diversification — holding different types of investments — reduces the blow. Cash savings provide a buffer.",
    deck: "event",
    illustrationKey: "gray",
    cashDelta: -20,
    wlbDelta: -2,
    affectsAll: true,
  },
  {
    id: "event_004",
    title: "Paid Family Leave",
    description: "Take time with your family — paid.",
    conceptExplanation: "Countries and companies with paid leave help workers maintain income without sacrificing family time. When evaluating a job, benefits matter as much as salary.",
    deck: "event",
    illustrationKey: "green",
    cashDelta: 10,
    wlbDelta: 3,
    affectsAll: false,
  },
  {
    id: "event_005",
    title: "Car Breakdown",
    description: "Your engine needs urgent repair.",
    conceptExplanation: "Sinking funds — money set aside monthly for predictable irregular costs like car repairs — prevent budget disruption. Plan for irregular expenses before they happen.",
    deck: "event",
    illustrationKey: "orange",
    cashDelta: -25,
    wlbDelta: -1,
    affectsAll: false,
    conditionIcon: "trade_cert",
  },
  {
    id: "event_006",
    title: "Tax Refund",
    description: "The government sends money back!",
    conceptExplanation: "A tax refund means you overpaid during the year — it's your own money returned, not a windfall. Smart tax planning can improve your cash flow all year round.",
    deck: "event",
    illustrationKey: "gold",
    cashDelta: 30,
    wlbDelta: 0,
    affectsAll: false,
  },
  {
    id: "event_007",
    title: "Rent Increase",
    description: "Your landlord raises rent by 15%.",
    conceptExplanation: "Renting means your housing cost is controlled by someone else. Owning a home locks in your payment. Understanding rent vs. buy trade-offs is a core financial skill.",
    deck: "event",
    illustrationKey: "red",
    cashDelta: -20,
    wlbDelta: -1,
    affectsAll: false,
  },
  {
    id: "event_008",
    title: "Bull Market",
    description: "Stock prices soar across the board!",
    conceptExplanation: "A bull market rewards investors who stayed in when things looked uncertain. Time in the market beats timing the market — patience is an investor's superpower.",
    deck: "event",
    illustrationKey: "green",
    cashDelta: 0,
    wlbDelta: 1,
    affectsAll: true,
  },
  {
    id: "event_009",
    title: "Job Layoff",
    description: "Your employer downsizes. You're let go.",
    conceptExplanation: "Even hardworking people get laid off. An emergency fund of 3–6 months of expenses buys you time to find the right next job rather than the first available one.",
    deck: "event",
    illustrationKey: "red",
    cashDelta: -35,
    wlbDelta: -3,
    affectsAll: false,
    conditionIcon: "network",
  },
  {
    id: "event_010",
    title: "Side Hustle Takes Off",
    description: "Your extra project brings in real money.",
    conceptExplanation: "Multiple income streams protect you. If one dries up, the others keep flowing. Starting a side hustle early — even small — builds this resilience.",
    deck: "event",
    illustrationKey: "gold",
    cashDelta: 35,
    wlbDelta: -1,
    affectsAll: false,
  },
  {
    id: "event_011",
    title: "Unexpected Bill",
    description: "Home appliance breaks down.",
    conceptExplanation: "Homeownership comes with maintenance costs averaging 1–2% of home value per year. Budgeting for this prevents emergencies from becoming financial crises.",
    deck: "event",
    illustrationKey: "orange",
    cashDelta: -15,
    wlbDelta: -1,
    affectsAll: false,
  },
  {
    id: "event_012",
    title: "Birthday Gift!",
    description: "Family sends you birthday money.",
    conceptExplanation: "A windfall — unexpected money — is a chance to invest rather than spend. Even a small investment compounded over 10 years can grow significantly.",
    deck: "event",
    illustrationKey: "pink",
    cashDelta: 20,
    wlbDelta: 2,
    affectsAll: false,
  },
  {
    id: "event_013",
    title: "Market Correction",
    description: "Investments lose 10% of their value.",
    conceptExplanation: "Market corrections are normal — they happen roughly once a year. Long-term investors don't panic-sell. Riding out corrections is how wealth is built.",
    deck: "event",
    illustrationKey: "orange",
    cashDelta: 0,
    wlbDelta: -1,
    affectsAll: true,
  },
  {
    id: "event_014",
    title: "Scholarship Award",
    description: "You win a surprise education grant.",
    conceptExplanation: "Scholarships and grants are free money for education — unlike student loans they don't need to be repaid. Applying for them is always worth the time.",
    deck: "event",
    illustrationKey: "purple",
    cashDelta: 25,
    wlbDelta: 2,
    affectsAll: false,
  },
  {
    id: "event_015",
    title: "Cost of Living Rise",
    description: "Inflation hits — everything costs more.",
    conceptExplanation: "Inflation erodes the value of cash sitting idle. Money invested in assets that grow faster than inflation — like stocks — protects your purchasing power over time.",
    deck: "event",
    illustrationKey: "red",
    cashDelta: -10,
    wlbDelta: -1,
    affectsAll: true,
  },
  {
    id: "event_016",
    title: "Company Profit Share",
    description: "Your employer shares the success.",
    conceptExplanation: "Profit-sharing and equity compensation are powerful benefits that build wealth over time. When evaluating a job offer, look beyond the salary to the total package.",
    deck: "event",
    illustrationKey: "gold",
    cashDelta: 40,
    wlbDelta: 1,
    affectsAll: false,
  },
  {
    id: "event_017",
    title: "Water Pipe Burst",
    description: "A plumbing disaster at home.",
    conceptExplanation: "Homeownership means you're the one paying for repairs. Home insurance covers big disasters, but you still pay deductibles. Always keep a home emergency fund.",
    deck: "event",
    illustrationKey: "orange",
    cashDelta: -30,
    wlbDelta: -2,
    affectsAll: false,
    conditionIcon: "trade_cert",
  },
  {
    id: "event_018",
    title: "Interest Rate Rise",
    description: "The central bank raises rates. Debt costs more.",
    conceptExplanation: "Central banks raise interest rates to slow inflation. This increases loan and mortgage costs but also means savings accounts pay more. Fixed-rate debt protects you.",
    deck: "event",
    illustrationKey: "gray",
    cashDelta: -15,
    wlbDelta: -1,
    affectsAll: true,
  },
  {
    id: "event_019",
    title: "Friend's Wedding",
    description: "You're in the wedding party. The fun is expensive.",
    conceptExplanation: "Social spending is real spending. Being a good friend doesn't mean going into debt. It's okay to set a budget for social events and communicate it honestly.",
    deck: "event",
    illustrationKey: "pink",
    cashDelta: -20,
    wlbDelta: 3,
    affectsAll: false,
  },
  {
    id: "event_020",
    title: "Viral Social Post",
    description: "Something you made blew up online.",
    conceptExplanation: "Virality can create real income — brand deals, merchandise, speaking invites. But it's unpredictable. Never rely on it as a primary income source.",
    deck: "event",
    illustrationKey: "gold",
    cashDelta: 35,
    wlbDelta: 2,
    affectsAll: false,
    conditionIcon: "skill_badge",
  },
  {
    id: "event_021",
    title: "Tech Boom",
    description: "Innovation drives the whole economy up.",
    conceptExplanation: "Sectors lead economic cycles. When tech booms, investors with exposure win big. Diversifying into growth sectors — even a small amount — builds long-term wealth.",
    deck: "event",
    illustrationKey: "green",
    cashDelta: 25,
    wlbDelta: 1,
    affectsAll: true,
  },
  {
    id: "event_022",
    title: "Identity Theft",
    description: "Someone used your financial details.",
    conceptExplanation: "Cybercrime costs billions yearly. Protecting your digital identity — strong passwords, two-factor auth, credit freezes — is as important as locking your front door.",
    deck: "event",
    illustrationKey: "red",
    cashDelta: -35,
    wlbDelta: -3,
    affectsAll: false,
    conditionIcon: "skill_badge",
  },
  {
    id: "event_023",
    title: "Free Online Course",
    description: "You completed a free certificate course.",
    conceptExplanation: "Free learning platforms like Coursera, edX, and Khan Academy have democratised education. A free course won't replace a degree, but it can demonstrate motivation and build skills.",
    deck: "event",
    illustrationKey: "purple",
    cashDelta: 0,
    wlbDelta: 2,
    affectsAll: false,
  },
  {
    id: "event_024",
    title: "Energy Bill Spike",
    description: "Winter hits hard. Heating costs soar.",
    conceptExplanation: "Fixed costs like utilities vary more than people expect. A home energy audit, better insulation, or solar panels are investments that reduce future living costs.",
    deck: "event",
    illustrationKey: "orange",
    cashDelta: -18,
    wlbDelta: -1,
    affectsAll: true,
  },
];
